import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { RevealLines } from "@/components/RevealText";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { EnquiryForm } from "@/components/EnquiryForm";
import { getProduct, PRODUCTS } from "@/data/products";
import { FAQS } from "@/data/faq";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Check, ArrowUpRight } from "lucide-react";

const ProductDetail = () => {
    const { slug } = useParams();
    const product = getProduct(slug);
    if (!product) return <Navigate to="/products" replace />;
    const nextProduct = PRODUCTS[(PRODUCTS.findIndex((p) => p.slug === slug) + 1) % PRODUCTS.length];

    return (
        <div data-testid={`product-detail-${slug}`}>
            <section className="pt-40 pb-16 border-b border-white/10">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
                    <Breadcrumbs items={[{ label: "Products", to: "/products" }, { label: product.name, to: `/products/${slug}` }]} />
                    <div className="mt-10 flex items-center gap-6">
                        <div className="font-editorial italic text-edge text-5xl">{product.index}</div>
                        <div className="h-px w-12 bg-edge" />
                        <span className="text-[10px] tracking-[0.35em] text-edge uppercase font-bold">Product</span>
                    </div>
                    <RevealLines
                        as="h1"
                        lines={[product.name]}
                        className="mt-8 font-display font-black uppercase tracking-tighter text-[12vw] md:text-[9vw] lg:text-[7vw] leading-[0.9]"
                    />
                    <p className="mt-8 text-xl md:text-2xl text-white/80 font-editorial italic max-w-2xl">
                        {product.tagline}
                    </p>
                </div>
            </section>

            <section className="border-b border-white/10">
                <div className="grid lg:grid-cols-12">
                    <div className="lg:col-span-7 relative aspect-[4/3] lg:aspect-auto overflow-hidden bg-ink-800">
                        <motion.img
                            initial={{ scale: 1.15 }}
                            whileInView={{ scale: 1 }}
                            transition={{ duration: 1.6, ease: [0.65, 0, 0.35, 1] }}
                            viewport={{ once: true }}
                            src={product.hero}
                            alt={product.name}
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-ink-950/40" />
                    </div>
                    <div className="lg:col-span-5 p-8 lg:p-16 border-l border-white/10">
                        <div className="text-[10px] tracking-[0.3em] text-edge uppercase">Overview</div>
                        <p className="mt-6 text-white/80 text-lg leading-relaxed">{product.overview}</p>
                        <div className="mt-10 space-y-3">
                            <div className="text-[10px] tracking-[0.3em] text-edge uppercase">Key Features</div>
                            {product.features.map((f, i) => (
                                <div key={i} className="flex items-start gap-3">
                                    <Check className="w-4 h-4 text-edge mt-1 shrink-0" strokeWidth={2.5} />
                                    <span className="text-white/85">{f}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-24 border-b border-white/10">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="text-[10px] tracking-[0.3em] text-edge uppercase mb-4">Benefits</div>
                        <h2 className="font-display uppercase text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-[0.95]">
                            What it means for your roof.
                        </h2>
                        <div className="mt-8 space-y-4">
                            {product.benefits.map((b, i) => (
                                <div key={i} className="flex items-start gap-3 border-t border-white/10 pt-4">
                                    <div className="font-editorial italic text-edge text-2xl leading-none w-8">{String(i + 1).padStart(2, "0")}</div>
                                    <span className="text-white/85">{b}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7">
                        <div className="text-[10px] tracking-[0.3em] text-edge uppercase mb-4">Technical Specifications</div>
                        <div className="border border-white/10">
                            {product.specs.map(([k, v], i) => (
                                <div key={i} className="grid grid-cols-2 border-b border-white/10 last:border-b-0">
                                    <div className="p-4 text-slateink text-sm uppercase tracking-wider">{k}</div>
                                    <div className="p-4 font-display font-bold border-l border-white/10">{v}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-24 border-b border-white/10">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="text-[10px] tracking-[0.3em] text-edge uppercase mb-4">Applications</div>
                        <h2 className="font-display uppercase text-3xl md:text-4xl font-black tracking-tight">Where we install it.</h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 grid sm:grid-cols-2 gap-[1px] bg-white/10">
                        {product.applications.map((a, i) => (
                            <div key={i} className="bg-ink-950 p-6">
                                <div className="font-editorial italic text-edge/60">{String(i + 1).padStart(2, "0")}</div>
                                <div className="font-display uppercase mt-2 tracking-tight">{a}</div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="max-w-[1600px] mx-auto px-6 lg:px-12 mt-20 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="text-[10px] tracking-[0.3em] text-edge uppercase mb-4">Installation Guide</div>
                        <h2 className="font-display uppercase text-3xl md:text-4xl font-black tracking-tight">How we do it right.</h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 text-white/80 leading-relaxed text-lg">
                        {product.installation}
                    </div>
                </div>
            </section>

            <section className="py-24 border-b border-white/10">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <div className="text-[10px] tracking-[0.3em] text-edge uppercase mb-4">FAQ</div>
                        <h2 className="font-display uppercase text-3xl md:text-4xl font-black tracking-tight">Common questions.</h2>
                    </div>
                    <div className="lg:col-span-7 lg:col-start-6">
                        <Accordion type="single" collapsible>
                            {FAQS.slice(0, 5).map((f, i) => (
                                <AccordionItem key={i} value={`f-${i}`} className="border-white/10">
                                    <AccordionTrigger className="text-left font-display uppercase tracking-tight py-6 hover:text-edge">{f.q}</AccordionTrigger>
                                    <AccordionContent className="text-white/70 pb-6">{f.a}</AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </section>

            <section className="py-24 border-b border-white/10">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="text-[10px] tracking-[0.3em] text-edge uppercase mb-4">Get a Free Quote</div>
                        <h2 className="font-display uppercase text-4xl md:text-5xl font-black tracking-tighter">
                            Want this on your roof?
                        </h2>
                        <p className="mt-4 text-white/70">We'll call back within business hours with a spec-backed proposal.</p>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 border border-white/10 bg-ink-900/50 p-8 lg:p-10">
                        <EnquiryForm variant="full" defaultInterest={product.name} source={`product-${slug}`} />
                    </div>
                </div>
            </section>

            <Link to={`/products/${nextProduct.slug}`} className="block bg-ink-900 hover:bg-ink-800 py-16 transition-colors group">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-12 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] tracking-[0.3em] text-edge uppercase">Next Product</div>
                        <div className="font-display uppercase text-3xl md:text-5xl font-black tracking-tighter mt-2">
                            {nextProduct.name}
                        </div>
                    </div>
                    <ArrowUpRight className="w-10 h-10 md:w-16 md:h-16 text-edge group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
            </Link>
        </div>
    );
};

export default ProductDetail;
